import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { useBetFiltersValidation } from '@/composables/useBetFiltersValidation';
import { BetDateType } from '@/constants';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { BetItem } from '@/models/bet';
import { BetFailItem } from '@/models/betFail';
import { createActivityPageHeaders } from '@/pages/admin/activity/ActivityPageHeaders';
import { activityService } from '@/services/activityService';
import { isRequestCanceled } from '@/services/baseService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IActivityFilters, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'createdAt', order: 'desc' as const }];

function createActivityStoreSetup(storeId: string) {
    // --- state ---
    const { isHandicapValid, isCorrectScoreValid } = useBetFiltersValidation();
    const items = shallowRef<(BetItem | BetFailItem)[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IActivityFilters>({
        sportId: createRequiredSportFilter(),
        betDateType: {
            value: BetDateType.PlacementLondon,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bets.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betSearch: {
            value: null,
            title: 'bets.betSearch',
            allowSaveState: false,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
        },
        betActivityType: {
            value: null,
            title: 'bets.activityType.title',
            allowSaveState: true,
        },
        betBookmakerId: {
            value: null,
            title: 'bets.bookmaker',
            allowSaveState: true,
        },
        betStatusId: {
            value: null,
            title: 'bets.status',
            allowSaveState: true,
        },
        betPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        userObserveId: {
            value: null,
            title: 'bets.userObserveId',
            allowSaveState: true,
        },
        userAgentId: {
            value: null,
            title: 'bets.agent',
            allowSaveState: true,
        },
        betUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        betBotId: {
            value: null,
            title: 'bets.bot',
            allowSaveState: true,
        },
        betError: {
            value: null,
            title: 'bets.errorName',
            allowSaveState: false,
        },
        betAccountId: {
            value: null,
            title: 'bets.account',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betSelectionId: {
            value: null,
            title: 'bets.betType',
            allowSaveState: true,
        },
        betCustomTeamId: {
            value: null,
            title: 'common.customTeam',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        betHandicap: {
            value: null,
            title: 'filters.handicap',
            allowSaveState: true,
        },
        betCorrectScore: {
            value: null,
            title: 'common.correctScore',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
        betOrderId: {
            value: null,
            title: 'orders.orderId',
            allowSaveState: false,
            forApiOnly: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.betDateType.value = BetDateType.PlacementLondon;
        filters.betDate.value = todayDateRangeInUserTimeZone();
        filters.betSearch.value = null;
        filters.betTradingTypeId.value = null;
        filters.betActivityType.value = null;
        filters.betBookmakerId.value = null;
        filters.betStatusId.value = null;
        filters.betPlatformId.value = null;
        filters.userObserveId.value = null;
        filters.userAgentId.value = null;
        filters.betUserId.value = null;
        filters.betBotId.value = null;
        filters.betError.value = null;
        filters.betAccountId.value = null;
        filters.betEventPeriodId.value = null;
        filters.betSelectionId.value = null;
        filters.betCustomTeamId.value = null;
        filters.betEventType.value = null;
        filters.betHandicap.value = null;
        filters.betCorrectScore.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
        filters.betOrderId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createActivityPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'activity',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        if (!isHandicapValid(filters.betHandicap.value)) return;
        if (!isCorrectScoreValid(filters.betCorrectScore.value)) return;

        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.betSearch.value) {
                filtersToSend.betDate = { ...filtersToSend.betDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Agent', 'Country'],
            };

            const response = await activityService.list(params);

            const bets: BetItem[] = [];
            const betFails: BetFailItem[] = [];

            response.data.rows.forEach((item) => {
                if (item.error) {
                    betFails.push(new BetFailItem(item));
                } else {
                    bets.push(new BetItem(item));
                }
            });

            const allItems: (BetItem | BetFailItem)[] = [...bets, ...betFails];

            allItems.sort((a, b) => {
                const aDate = new Date(a.createdAt).getTime();
                const bDate = new Date(b.createdAt).getTime();
                return bDate - aDate;
            });

            items.value = allItems;
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function useActivityStore(storeId: string) {
    const useStore = defineStore(storeId, () => createActivityStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'activity'),
    });
    return useStore();
}

export type ActivityStoreSetup = ReturnType<typeof useActivityStore>;
